// ── Feature Pack A — opt-in helpers ──────────────────────────────────────────
// Each function is a thin wrapper around an RPC or table call. Components
// should still gate UI on `features.feature_<key>` from AppContext before
// calling these — the SQL functions also gate-check the flag for safety.

import { supabase, db } from './supabase';

// ── A. Specific friend invites ───────────────────────────────────────────────
export async function logFriendInvite({ inviterId, friendName, friendEmail, friendPhone, channel = 'mailto', source = 'onboarding' }) {
  if (!supabase || !inviterId) return { error: 'not ready' };
  const { error } = await db.friend_invites_sent()?.insert({
    inviter_id:   inviterId,
    friend_name:  friendName?.trim() || null,
    friend_email: friendEmail?.trim()?.toLowerCase() || null,
    friend_phone: friendPhone?.trim() || null,
    channel, source,
  }) || { error: null };
  return { error: error?.message || null };
}

export function buildPersonalInviteText({ friendName, inviterName, link, lang = 'en' }) {
  const fr = lang === 'fr';
  const f  = friendName?.trim() || (fr ? 'toi' : 'you');
  const i  = inviterName?.split(' ')[0] || (fr ? 'moi' : 'me');
  if (fr) {
    return `Hey ${f}, je teste BeeLove — une app où ce sont les amis qui décrivent qui tu es vraiment. Tu peux répondre à 10 questions sur ${i} ? Ça prend 2 min : ${link}`;
  }
  return `Hey ${f}, I'm trying BeeLove — an app where friends describe who you really are. Could you answer 10 questions about ${i}? Takes 2 min: ${link}`;
}

export function buildMailto({ friendEmail, subject, body }) {
  const u = new URLSearchParams({ subject: subject || '', body: body || '' });
  return `mailto:${friendEmail || ''}?${u.toString().replace(/\+/g, '%20')}`;
}

export function buildSmsLink({ phone, body }) {
  // iOS uses ?body=, Android tolerates both — encode the body URI-safe.
  const sep = /Android/i.test(navigator?.userAgent || '') ? '?' : '&';
  return `sms:${phone || ''}${phone ? sep : ''}${phone ? 'body=' : ''}${encodeURIComponent(body || '')}`;
}

// ── C. Friend quote preview (public, anon-safe) ──────────────────────────────
export async function fetchFriendQuotePreview(targetId) {
  if (!supabase || !targetId) return null;
  const { data } = await supabase.rpc('get_friend_quote_preview', { p_target: targetId });
  return data?.[0]?.quote || null;
}

// ── E. Rating streak ─────────────────────────────────────────────────────────
export async function fetchRatingStreak(userId) {
  if (!supabase || !userId) return 0;
  const { data } = await supabase.rpc('get_rating_streak', { p_user: userId });
  return Number(data) || 0;
}

// ── G. Date check-ins ────────────────────────────────────────────────────────
export async function createDateCheckin({ userId, matchId, partnerName, trustedId, trustedEmail, dateAt, location, notes }) {
  if (!supabase || !userId) return { error: 'not ready' };
  const { data, error } = await db.date_checkins()?.insert({
    user_id:        userId,
    match_id:       matchId || null,
    partner_name:   partnerName || null,
    trusted_id:     trustedId || null,
    trusted_email:  trustedEmail?.trim()?.toLowerCase() || null,
    date_at:        dateAt,
    location:       location || null,
    notes:          notes || null,
  }).select('id').single() || { data: null, error: null };
  return { id: data?.id || null, error: error?.message || null };
}

export async function loadMyDateCheckins(userId, { upcoming = true } = {}) {
  if (!supabase || !userId) return [];
  let q = db.date_checkins()?.select('*').eq('user_id', userId).order('date_at', { ascending: !upcoming });
  if (upcoming) q = q.gte('date_at', new Date().toISOString()).in('status', ['scheduled', 'checked_in']);
  const { data } = (await q) || {};
  return data || [];
}

export async function updateDateCheckinStatus(checkinId, status) {
  if (!supabase || !checkinId) return { error: 'not ready' };
  const patch = { status };
  if (status === 'safe' || status === 'cancelled') patch.resolved_at = new Date().toISOString();
  const { error } = await db.date_checkins()?.update(patch).eq('id', checkinId) || { error: null };
  return { error: error?.message || null };
}

// ── I. Friend overlap ────────────────────────────────────────────────────────
export async function fetchFriendOverlap(viewerId, targetId) {
  if (!supabase || !viewerId || !targetId) return [];
  const { data } = await supabase.rpc('get_friend_overlap', { p_viewer: viewerId, p_target: targetId });
  return data || [];
}

// ── L. Hive pulse ────────────────────────────────────────────────────────────
export async function fetchHivePulse(networkId) {
  if (!supabase || !networkId) return null;
  const { data } = await supabase.rpc('get_hive_pulse', { p_network: networkId });
  return data || null;
}

// ── N. Coffee Roulette ───────────────────────────────────────────────────────
export async function joinCoffeeRoulette({ userId, networkId, hiveGroup, slotDates }) {
  if (!supabase || !userId) return { error: 'not ready' };
  const { error } = await db.coffee_roulette()?.upsert({
    user_id:    userId,
    network_id: networkId || 'ca_montreal',
    hive_group: hiveGroup,
    slot_dates: Array.isArray(slotDates) ? slotDates : [],
    status:     'waiting',
  }, { onConflict: 'user_id,network_id,hive_group' }) || { error: null };
  return { error: error?.message || null };
}

export async function leaveCoffeeRoulette({ userId, networkId, hiveGroup }) {
  if (!supabase || !userId) return { error: 'not ready' };
  const { error } = await db.coffee_roulette()
    ?.update({ status: 'cancelled' })
    .eq('user_id', userId)
    .eq('network_id', networkId || 'ca_montreal')
    .eq('hive_group', hiveGroup) || { error: null };
  return { error: error?.message || null };
}

export async function loadCoffeeStatus({ userId, networkId, hiveGroup }) {
  if (!supabase || !userId || !hiveGroup) return null;
  const { data } = await db.coffee_roulette()
    ?.select('*')
    .eq('user_id', userId)
    .eq('network_id', networkId || 'ca_montreal')
    .eq('hive_group', hiveGroup)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle() || {};
  return data || null;
}

// ── O. CML leaderboard (public) ──────────────────────────────────────────────
export async function fetchCmlLeaderboard() {
  if (!supabase) return [];
  const { data } = await supabase.rpc('get_cml_leaderboard');
  return data || [];
}

// ── Q. Scout sponsors ────────────────────────────────────────────────────────
export async function loadScoutSponsors(scoutId) {
  if (!supabase || !scoutId) return [];
  const { data } = await db.scout_sponsors()
    ?.select('contribution, perk_description, sponsor:sponsors(id,name,logo_url,category)')
    .eq('scout_id', scoutId) || {};
  return data || [];
}

export async function attachSponsorToScout({ scoutId, sponsorId, contribution = 0, perkDescription = null }) {
  if (!supabase || !scoutId || !sponsorId) return { error: 'not ready' };
  const { error } = await db.scout_sponsors()?.upsert({
    scout_id:         scoutId,
    sponsor_id:       sponsorId,
    contribution,
    perk_description: perkDescription,
  }, { onConflict: 'scout_id,sponsor_id' }) || { error: null };
  return { error: error?.message || null };
}

export async function detachSponsorFromScout({ scoutId, sponsorId }) {
  if (!supabase || !scoutId || !sponsorId) return { error: 'not ready' };
  const { error } = await db.scout_sponsors()
    ?.delete()
    .eq('scout_id', scoutId)
    .eq('sponsor_id', sponsorId) || { error: null };
  return { error: error?.message || null };
}
